// docs/62 — the emote catalogue. `content/emotes.json` is the one list: an emote's wire id is its
// index (the module's `fk_sfx::emote::EMOTES` follows the same order, checked by a Rust test). The
// first 8 are the wheel's inner ring, the next 24 its outer ring; after them come a bard's own
// musical emotes (docs/104 BD6), offered only for a bard, in a row of their own. The atlas
// (`tools/emote-atlas.py`) holds one row per emote, its 16 frames left to right.
import catalogue from '../../../../../content/emotes.json';
import { deviceNow } from '../time';
import { emoteId, hotkeyFor, type Overrides } from '../input/hotkeys';

/** `hotkey` is the emote's hotkey id (`core/input/hotkeys.ts`, docs/87); its keys are the player's to rebind. */
export interface EmoteDef { id: number; key: string; name: string; frame: number; hotkey: string; ring: 'inner' | 'outer' | 'bard'; bard: boolean }

export const EMOTE_ATLAS = '/assets/emotes/emotes.png';
export const EMOTE_PX: number = catalogue.frame_px;
export const EMOTE_FRAMES: number = catalogue.frames;
/** Milliseconds per frame (`fk_sfx::emote::FRAME_MS`: the server times the sound to these frames). */
export const FRAME_MS = 90;
/** The hotkey held to open the wheel (EM3; T by default). */
export const WHEEL_HOTKEY = 'emote.wheel';
export const INNER_COUNT = 8;
/** `hero.class` of a bard (`ClassId::Bard`, docs/104 NC1). */
export const BARD_CLASS = 7;

/** Inner ring on the number row, outer ring on the letter rows (the defaults live in `core/input/hotkeys.ts`);
 *  a bard's own emotes (`bard: true` in the catalogue) sit in a row under the wheel. */
export const EMOTES: EmoteDef[] = catalogue.emotes.map((e, id) => {
  const bard = 'bard' in e && e.bard === true;
  return { id, key: e.key, name: e.name, frame: e.frame, hotkey: emoteId(e.key), ring: bard ? 'bard' : id < INNER_COUNT ? 'inner' : 'outer', bard };
});
/** Every hero's emotes (the two rings). */
export const RING_EMOTES: EmoteDef[] = EMOTES.filter((e) => !e.bard);
/** A bard's own musical emotes (docs/104 BD6): the module refuses them from any other class. */
export const BARD_EMOTES: EmoteDef[] = EMOTES.filter((e) => e.bard);

/** The emotes the wheel offers a hero: a bard gets their own row as well. */
export const emotesFor = (bard: boolean): EmoteDef[] => (bard ? EMOTES : RING_EMOTES);

/** The emote a key press picks while the wheel is open (a bard's own only for a bard). */
export function emoteForKey(e: Pick<KeyboardEvent, 'code' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>, overrides?: Overrides, bard = false): EmoteDef | undefined {
  const list = emotesFor(bard);
  const id = hotkeyFor(e, list.map((d) => d.hotkey), overrides);
  return id ? list.find((d) => d.hotkey === id) : undefined;
}

/**
 * The frames an emote plays, in order: it pops in (0–2), runs its loop (3–13) twice and pops out
 * (14–15). Frame f of the first loop is at index f, so the server's sound frames line up.
 */
export const SEQUENCE: number[] = [0, 1, 2, ...range(3, 14), ...range(3, 14), 14, 15];
export const EMOTE_MS = SEQUENCE.length * FRAME_MS;

function range(a: number, b: number): number[] { return Array.from({ length: b - a }, (_, i) => a + i); }

/** Atlas frame index (row × frames + column) of emote `id` at frame `f`. */
export const atlasFrame = (id: number, f: number): number => id * EMOTE_FRAMES + f;

/** The frame an emote that began at `at` (device ms) shows now; undefined once it has played out. */
export function frameAt(at: number, now = deviceNow()): number | undefined {
  const i = Math.floor((now - at) / FRAME_MS);
  return i < 0 ? 0 : i < SEQUENCE.length ? SEQUENCE[i] : undefined;
}

/** A pet emote (docs/62, docs/59): a ranger's companion's own small wheel. Its wire id is its index
 *  (`fk_sfx::emote::PET_EMOTES`, append only). No bubble: the beast wiggles while its sound plays. */
export interface PetEmoteDef { id: number; key: 'happy' | 'alert' | 'play' | 'comfort'; name: string; glyph: string; hotkey: string }

/** On the keys of the hero wheel's first four slots (1–4 by default), clockwise from the top. */
export const PET_EMOTES: PetEmoteDef[] = ([
  ['happy', 'Happy', '♥'],
  ['alert', 'Alert', '!'],
  ['play', 'Play', '✦'],
  ['comfort', 'Comfort', '☾'],
] as const).map(([key, name, glyph], id) => ({ id, key, name, glyph, hotkey: EMOTES[id]!.hotkey }));

/** How long a pet's wiggle runs (`fk_sfx::emote::PET_EMOTE_MS`). */
export const PET_EMOTE_MS = 1100;

/** The pet emote a key press picks while a companion's wheel is open. */
export function petEmoteForKey(e: Pick<KeyboardEvent, 'code' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>, overrides?: Overrides): PetEmoteDef | undefined {
  const id = hotkeyFor(e, PET_EMOTES.map((p) => p.hotkey), overrides);
  return id ? PET_EMOTES.find((p) => p.hotkey === id) : undefined;
}
